import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import OntologyBrowser, {
    CodeText, OntologyColumn, ScopeLevelChip, SCOPE_LEVEL_FILTER, StatusChip, STATUS_FILTER,
} from './OntologyBrowser';
import ConceptCategoryRail from './ConceptCategoryRail';
import InForceCell, { InForceContext, InForceEntry } from './InForceCell';
import { categoryForParam, conceptTypeLabel } from './ontologyFormat';
import {
    getInForceByConcept, getOntologySummary, normalizeInForce, normalizeSummary,
} from '../../api/ontologyApi';
import { dsVar } from '../../theme/tokens';

/**
 * The Risk Library.
 *
 * `concept_type` is navigation here, not a filter — see ConceptCategoryRail. What changes
 * with the category is not only which rows appear but WHICH COLUMNS: the five generic
 * columns this screen used to show for every type told an investigator nothing about a red
 * flag that it did not also tell a tuning analyst about a threshold. A red flag's useful
 * second column is what detects it; a control's is the policy that sets it; a measure's is
 * how it is computed.
 */

// ─── Cells ───────────────────────────────────────────────────────────────────

const Clamped: React.FC<{ children: React.ReactNode; lines?: number }> = ({
    children, lines = 2,
}) => (
    <Typography
        component="div"
        sx={{
            fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.55,
            display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        }}
    >
        {children}
    </Typography>
);

const Plain: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
        {children}
    </Typography>
);

const NAME_COLUMN: OntologyColumn = {
    key: 'label',
    label: 'Name',
    width: '32%',
    render: (doc) => (
        <Box>
            <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                {doc.preferred_label || doc.concept_code}
            </Typography>
            <CodeText>{doc.concept_code}</CodeText>
        </Box>
    ),
};

const SCOPE_COLUMN: OntologyColumn = {
    key: 'scope_level',
    label: 'Scope',
    render: (doc) => <ScopeLevelChip scopeLevel={doc.scope_level} />,
};

const STATUS_COLUMN: OntologyColumn = {
    key: 'status',
    label: 'Status',
    render: (doc) => <StatusChip status={doc.status} />,
};

/** `guidance.what_it_means` is written for a human; `definition` is the formal wording. */
const meaningOf = (doc: Record<string, any>): string =>
    doc.guidance?.what_it_means || doc.definition || '—';

const countOf = (value: unknown): number => (Array.isArray(value) ? value.length : 0);

const listOf = (value: unknown): string =>
    (Array.isArray(value) && value.length ? value.join(', ') : '—');

// ─── Column sets ─────────────────────────────────────────────────────────────

/**
 * The Type column appears ONLY in the default set. Inside a category every row has the
 * same type, so a column repeating it is a column of one value — and the rail already
 * says which category you are in.
 */
const DEFAULT_COLUMNS: OntologyColumn[] = [
    NAME_COLUMN,
    {
        key: 'definition',
        label: 'Definition',
        width: '32%',
        render: (doc) => <Clamped>{doc.definition || '—'}</Clamped>,
    },
    {
        key: 'concept_type',
        label: 'Type',
        render: (doc) => <Plain>{conceptTypeLabel(doc.concept_type)}</Plain>,
    },
    SCOPE_COLUMN,
    STATUS_COLUMN,
];

const COLUMNS_BY_CATEGORY: Record<string, OntologyColumn[]> = {
    red_flags: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it means',
            width: '36%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        {
            key: 'detected_by',
            label: 'Detected by',
            render: (doc) => <Plain>{listOf(doc.detected_by)}</Plain>,
        },
        STATUS_COLUMN,
    ],

    typologies: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it looks like',
            width: '38%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        {
            key: 'relations',
            label: 'Related terms',
            render: (doc) => {
                const count = countOf(doc.relations);
                return <Plain>{count ? `${count} linked` : '—'}</Plain>;
            },
        },
        STATUS_COLUMN,
    ],

    controls: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it controls',
            width: '28%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        {
            // The platform's suggested value, from the concept's own tuning block.
            key: 'default_value',
            label: 'Default',
            render: (doc) => {
                const value = doc.tuning?.default_value;
                return <Plain>{value === undefined || value === null || value === '' ? '—' : String(value)}</Plain>;
            },
        },
        {
            // What this institution actually runs — a resolved parameter, not a concept
            // field, so it arrives from a per-page batch request and reaches the cell
            // through InForceContext. Sits beside the default deliberately: the pair reads
            // as "suggested / chosen", and a control showing "Not set" next to a default is
            // exactly the gap the coverage report counts.
            key: 'in_force',
            label: 'In force',
            render: (doc) => <InForceCell conceptCode={doc.concept_code} />,
        },
        SCOPE_COLUMN,
        STATUS_COLUMN,
    ],

    measures: [
        NAME_COLUMN,
        {
            key: 'definition',
            label: 'What it measures',
            width: '34%',
            render: (doc) => <Clamped>{doc.definition || meaningOf(doc)}</Clamped>,
        },
        {
            key: 'computed',
            label: 'How it is computed',
            render: (doc) => (
                <CodeText>{doc.measurement?.feature_path || doc.measurement?.profile_column || '—'}</CodeText>
            ),
        },
        STATUS_COLUMN,
    ],

    case_statuses: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it means',
            width: '44%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        SCOPE_COLUMN,
        STATUS_COLUMN,
    ],

    screening: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it covers',
            width: '44%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        SCOPE_COLUMN,
        STATUS_COLUMN,
    ],

    ratings: [
        NAME_COLUMN,
        {
            key: 'meaning',
            label: 'What it means',
            width: '34%',
            render: (doc) => <Clamped>{meaningOf(doc)}</Clamped>,
        },
        {
            key: 'band',
            label: 'Range',
            render: (doc) => {
                const range = doc.tuning?.value_range || doc.measurement?.value_range;
                if (!range) return <Plain>—</Plain>;
                return <Plain>{Array.isArray(range) ? range.join(' – ') : String(range)}</Plain>;
            },
        },
        // Severity bands and priority tiers share this rail entry, so the Type column
        // earns its place here — it is the one category where rows genuinely differ.
        {
            key: 'concept_type',
            label: 'Kind',
            render: (doc) => <Plain>{conceptTypeLabel(doc.concept_type)}</Plain>,
        },
        STATUS_COLUMN,
    ],

    entities: DEFAULT_COLUMNS,
};

// ─── Browser ─────────────────────────────────────────────────────────────────

interface ConceptsBrowserProps {
    filters: Record<string, string>;
    setFilters: (patch: Record<string, string>) => void;
    onRowSelect?: (doc: Record<string, any>) => void;
    selectedCode?: string | null;
}

const ConceptsBrowser: React.FC<ConceptsBrowserProps> = ({
    filters, setFilters, onRowSelect, selectedCode,
}) => {
    const [counts, setCounts] = useState<{ byType: Record<string, number>; total: number } | null>(null);

    // Rail counts. A failure here is silent by design: the rail must still navigate, and
    // an error banner about counts above a working table would be noise.
    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        (async () => {
            try {
                const res = await getOntologySummary({ signal: controller.signal });
                if (!active) return;
                const summary = normalizeSummary(res);
                setCounts({ byType: summary.concepts.byType, total: summary.concepts.total });
            } catch {
                if (active) setCounts(null);
            }
        })();
        return () => { active = false; controller.abort(); };
    }, []);

    const activeParam = filters.concept_type ?? '';
    const category = categoryForParam(activeParam);
    const columns = category
        ? COLUMNS_BY_CATEGORY[category.id] ?? DEFAULT_COLUMNS
        : DEFAULT_COLUMNS;

    // ── In-force thresholds for the visible page ──
    //
    // Only Detection Controls shows this column, so only that category pays for the
    // request. One call per page of rows; the codes come from the page that just loaded
    // because OntologyBrowser, not this component, owns the documents.
    const wantsInForce = category?.id === 'controls';
    const [inForce, setInForce] = useState<Record<string, InForceEntry> | null>(null);

    const handleDocumentsLoaded = useCallback((documents: Record<string, any>[]) => {
        if (!wantsInForce) { setInForce(null); return; }

        const codes = documents.map((doc) => doc.concept_code).filter(Boolean);
        if (!codes.length) { setInForce({}); return; }

        const controller = new AbortController();
        (async () => {
            try {
                const res = await getInForceByConcept(codes, { signal: controller.signal });
                setInForce(normalizeInForce(res));
            } catch {
                // Silent, like the rail counts: every other column is correct without this
                // one, and an error banner over a working table would be noise.
                setInForce(null);
            }
        })();
    }, [wantsInForce]);

    // Leaving the category must clear the map, or a stale page of control values would sit
    // under a category whose rows are not controls at all.
    useEffect(() => { if (!wantsInForce) setInForce(null); }, [wantsInForce]);

    const rail = (
        <ConceptCategoryRail
            value={activeParam}
            onChange={(next) => setFilters({ concept_type: next })}
            countsByType={counts?.byType}
            total={counts?.total}
        />
    );

    // The heading names where you are and, more importantly, says what the category IS.
    // This is the page's one chance to teach rather than assume.
    const toolbar = (
        <Box sx={{ mb: 2 }}>
            <Typography
                component="h2"
                sx={{ fontSize: '1rem', fontWeight: 600, color: dsVar('fg-body') }}
            >
                {category ? category.label : 'All entries'}
            </Typography>
            <Typography
                sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.25, maxWidth: '48rem', lineHeight: 1.6 }}
            >
                {category
                    ? category.subtitle
                    : 'Everything your agents reason with — red flags, typologies, controls, measures and the terms that describe them.'}
            </Typography>
        </Box>
    );

    return (
        <InForceContext.Provider value={inForce}>
            <OntologyBrowser
                kind="concepts"
                filters={filters}
                columns={columns}
                filterControls={[SCOPE_LEVEL_FILTER, STATUS_FILTER]}
                searchPlaceholder="Search names, codes and definitions"
                emptyMessage={category
                    ? `No ${category.label.toLowerCase()} match these filters.`
                    : 'No entries match these filters.'}
                rail={rail}
                toolbar={toolbar}
                onRowSelect={onRowSelect}
                selectedCode={selectedCode}
                onDocumentsLoaded={handleDocumentsLoaded}
            />
        </InForceContext.Provider>
    );
};

export default ConceptsBrowser;
